import type { Document } from "../document/Document";
import type { Layer } from "../document/Layer";
import type { Command } from "./Command";

// Set a single layer's position. Stored as absolute from/to so it's
// idempotent — re-applying the command lands at the same position.
//
// For group layers the command offsets every descendant leaf layer by the
// same (dx, dy) delta so the whole group moves as a unit. The group's own
// x/y is updated as a position-tracking field used by the undo mechanism;
// renderers read children positions directly.
//
// Used by MoveTool for all layer kinds (#216 fix).

export class MoveLayerCommand implements Command {
  readonly kind = "move-layer";
  constructor(
    private readonly layerId: string,
    private readonly fromX: number,
    private readonly fromY: number,
    private readonly toX: number,
    private readonly toY: number,
  ) {}

  apply(doc: Document): Document {
    return { ...doc, layers: setPos(doc.layers, this.layerId, this.toX, this.toY) };
  }

  invert(_before: Document, _after: Document): Command {
    return new MoveLayerCommand(this.layerId, this.toX, this.toY, this.fromX, this.fromY);
  }
}

/**
 * Recursively offset all leaf-layer positions within a subtree by (dx, dy).
 * Used for group moves where each child must shift independently.
 */
function offsetChildren(layers: readonly Layer[], dx: number, dy: number): Layer[] {
  return layers.map((l) => {
    if (l.kind === "group") {
      return { ...l, children: offsetChildren(l.children, dx, dy) };
    }
    return { ...l, x: l.x + dx, y: l.y + dy };
  });
}

/**
 * Set a single layer's absolute position, offsetting group children the same
 * way MoveLayerCommand.apply does. Exported so other commands that need to
 * reposition an arbitrary layer (e.g. MoveLayersCommand, and the extra-layer
 * handling in FloatSelectionCommand / MoveLayerAndSelectionCommand for
 * multi-select and linked-layer moves, audit i4/i56) can reuse the same
 * group-aware logic instead of duplicating it.
 */
export function setPos(layers: readonly Layer[], id: string, x: number, y: number): Layer[] {
  return layers.map((l) => {
    if (l.id === id) {
      if (l.kind === "group") {
        // For groups: compute the delta from the current tracking x/y and shift
        // all children by that amount. Also update the group's own x/y so the
        // undo mechanism (which stores absolute from/to) round-trips correctly.
        const dx = x - l.x;
        const dy = y - l.y;
        return { ...l, x, y, children: offsetChildren(l.children, dx, dy) };
      }
      return { ...l, x, y };
    }
    if (l.kind === "group") return { ...l, children: setPos(l.children, id, x, y) };
    return l;
  });
}
